/**
 * Chunked BLE write: 180 bytes per write, 5–20 ms delay, retries, progress, cancel.
 * Uses Web Bluetooth writeValue / writeValueWithoutResponse.
 */

import { writeChunk, writeAll } from './bleClient';

// Re-export writeAll for direct use when retries/progress aren't needed
export { writeAll };

const CHUNK_SIZE = 180;
const DELAY_MS = 15;
const MAX_RETRIES = 3;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export interface ChunkWriterOptions {
  onProgress?: (chunkIndex: number, totalChunks: number) => void;
  signal?: AbortSignal;
}

export async function writePayload(
  payload: Uint8Array,
  options: ChunkWriterOptions = {}
): Promise<void> {
  const { onProgress, signal } = options;
  const totalChunks = Math.ceil(payload.length / CHUNK_SIZE);

  for (let i = 0; i < totalChunks; i++) {
    if (signal?.aborted) throw new Error('Cancelled');
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, payload.length);
    const chunk = payload.slice(start, end);

    let lastError: Error | null = null;
    for (let retry = 0; retry < MAX_RETRIES; retry++) {
      try {
        await writeChunk(chunk);
        lastError = null;
        break;
      } catch (e) {
        lastError = e instanceof Error ? e : new Error(String(e));
        if (retry < MAX_RETRIES - 1) await delay(50 * (retry + 1));
      }
    }
    if (lastError) throw lastError;

    onProgress?.(i + 1, totalChunks);
    if (i < totalChunks - 1) await delay(DELAY_MS);
  }
}
